'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Label } from '@/shared/ui/shadcn/label';
import { useDebouncedCommit } from '../hooks/use-debounced-commit';

/** Server-side validation cap (EstimateUpdateRequest max:3000). */
const MAX_LENGTH = 3000;
const SAVE_DEBOUNCE_MS = 800;

interface EstimateSpecialRequirementsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current saved value (estimate.special_requirements). */
  value: string | null;
  /** Persist the new value (null clears it). Never flips `is_edited`. */
  onSave: (value: string | null) => void;
}

/**
 * Special-requirements drill-down for the estimate settings row: a free-text
 * LLM prompt scoped to the cost estimate (preferred material brands, works to
 * include/exclude). Goals-editor autosave pattern: a plain local buffer
 * seeded on open, debounced PATCHes while typing, flush on blur/close —
 * server updates are deliberately NOT pulled back into the field while open
 * so a refetch can't trample what the user is typing.
 */
export function EstimateSpecialRequirementsSheet({
  open,
  onOpenChange,
  value,
  onSave,
}: EstimateSpecialRequirementsSheetProps) {
  const t = useTranslations('common.estimate.editor');

  const [text, setText] = useState(value ?? '');
  // Last value handed to onSave — guards repeat flushes (blur + close both
  // fire) from issuing duplicate PATCHes with the same payload.
  const lastSavedRef = useRef(value ?? '');

  // Re-seed the local buffer each time the sheet opens (freshness comes on
  // open, not while typing). `value` is read on the open transition only.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setText(value ?? '');
      lastSavedRef.current = value ?? '';
    }
    wasOpenRef.current = open;
  }, [open, value]);

  const commit = useDebouncedCommit<string>((_key, next) => {
    const trimmed = next.trim();
    if (trimmed === lastSavedRef.current.trim()) return;
    lastSavedRef.current = next;
    onSave(trimmed === '' ? null : trimmed);
  }, SAVE_DEBOUNCE_MS);

  const handleChange = (next: string) => {
    setText(next);
    commit.schedule('special_requirements', next);
  };

  // Fire any pending save immediately (blur / sheet close) — the debounce
  // timer is cleared by flush, so no stale payload can fire afterwards.
  const flushSave = () => {
    commit.flush('special_requirements');
  };

  return (
    <NestedSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) flushSave();
        onOpenChange(next);
      }}
    >
      <NestedSheetContent>
        <NestedSheetHeader>
          <NestedSheetTitle>{t('specialRequirements')}</NestedSheetTitle>
        </NestedSheetHeader>

        <NestedSheetBody className="px-4">
          <div className="space-y-2 py-2">
            <Label htmlFor="estimate-special-requirements" className="sr-only">
              {t('specialRequirements')}
            </Label>
            <Textarea
              id="estimate-special-requirements"
              value={text}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={flushSave}
              placeholder={t('specialRequirementsPlaceholder')}
              rows={6}
              maxLength={MAX_LENGTH}
              className="min-h-[200px] resize-none"
            />
            <p className="text-form-help text-muted-foreground">
              {t('specialRequirementsHint')}
            </p>
          </div>
        </NestedSheetBody>
      </NestedSheetContent>
    </NestedSheet>
  );
}
